import { z } from "zod";

import shipped from "../../../crates/project-templates/samples.json" with { type: "json" };

import type { StartingDrawing } from "./starting-drawings";

/**
 * The sample projects (FR-PROJ-009) and their drawings: the same file the identity service's gallery and the doc
 * service's `@sample/<sample>/<drawing>` starts read (`crates/project-templates`), checked here when first needed. The
 * demo backend's sample project lists the villa's drawings from it, and Draft starts a sample project's drawing from
 * the doc service's copy.
 */

const sampleSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string(),
  drawings: z
    .array(
      z.object({
        slug: z.string().min(1),
        name: z.string().min(1),
        kind: z.enum(["plan", "site", "section", "elevation", "detail", "layout"]),
      }),
    )
    .min(1),
});
const samplesSchema = z.object({ version: z.number().int(), samples: z.array(sampleSchema) });

export type SampleSpec = z.infer<typeof sampleSchema>;

let parsed: z.infer<typeof samplesSchema> | undefined;

/** The shipped samples, parsed once; a bad file throws, listing what is wrong. */
export function sampleSpecs(): readonly SampleSpec[] {
  parsed ??= samplesSchema.parse(shipped);
  return parsed.samples;
}

/** The sample whose project is the first one the gallery adds: the villa. */
export const FIRST_SAMPLE_ID = "villa";

/** A sample's drawings as a project lists them; none for an unknown sample. */
export function sampleDrawings(sampleId: string): StartingDrawing[] {
  const sample = sampleSpecs().find((candidate) => candidate.id === sampleId);
  return (sample?.drawings ?? []).map(({ slug, name, kind }) => ({ id: slug, name, kind }));
}
